import { ClockIcon, EyeIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { DateTypeDJ } from '../../../@types/Company';
import { getImportSourceBadgeMode, getImportSourceLabel, IMPORT_SOURCE_OPTIONS } from '../../../@types/ImportSource';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import NotLatestTestImport from '../../common/NotLatestTestImport';
import Spinner from '../../common/Spinner';
import CompanySearch from '../components/CompanySearch';

interface CompanySubscriptions {
  communicating?: { startDate: DateTypeDJ | null; endDate: DateTypeDJ | null };
  prospecting?: { startDate: DateTypeDJ | null; endDate: DateTypeDJ | null };
  monitoring?: { startDate: DateTypeDJ | null; endDate: DateTypeDJ | null };
}

interface Company {
  id: number;
  name: string;
  siren: string;
  paidAccessUntil?: string;
  subscriptions?: CompanySubscriptions;
  commission?: number;
}

interface User {
  id: number;
  firstname: string;
  lastname: string;
}

interface ImportDataSummary {
  total?: number;
  totalInFile?: number;
  totalOk?: number;
  totalWeHave?: number;
  totalNotInRnic?: number;
  inserted?: Record<string, unknown>;
  updated?: Record<string, unknown>;
  errors?: Record<string, unknown>;
  summary?: { totalInFile?: number; totalOk?: number; totalWeHave?: number; totalNotInRnic?: number };
  coprosOk?: unknown;
}

interface Import {
  id: number;
  type: number;
  data: ImportDataSummary | null;
  company: Company;
  createdBy: User;
  createdAt: string;
  isTestImport: boolean;
  importSource: string | null;
}

/**
 * Detect if data is a comparison result (test import) or a regular import result.
 * Comparison results have 'summary' and 'coprosOk' fields.
 */
const isComparisonResult = (data: ImportDataSummary | null): data is ImportDataSummary & { summary: object; coprosOk: unknown } => {
  if (!data) {return false;}
  return 'summary' in data && 'coprosOk' in data;
};

const ImportHistory: React.FC = () => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const navigate = useNavigate();
  const [imports, setImports] = useState<Import[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterTestImport, setFilterTestImport] = useState<string>('');
  const [filterCompanyId, setFilterCompanyId] = useState<number | null>(null);
  const [companySearchResetKey, setCompanySearchResetKey] = useState(0);
  
  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [pendingNavigationId, setPendingNavigationId] = useState<number | null>(null);

  const fetchImports = useCallback(async() => {
    if (!jwt) {return;}

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSource) {
        params.append('importSource', filterSource);
      }
      if (filterTestImport !== '') {
        params.append('isTestImport', filterTestImport);
      }
      if (filterCompanyId !== null) {
        params.append('companyId', String(filterCompanyId));
      }

      const url = `/api/imports${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Failed to fetch imports');
      }

      const data = await response.json();
      setImports(data);
    } catch (error) {
      console.error('Error fetching imports:', error);
      showToast('error', 'Erreur lors du chargement des imports.');
    } finally {
      setLoading(false);
    }
  }, [jwt, filterSource, filterTestImport, filterCompanyId]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const formatDate = (dateString: string) => {
    if (!dateString) {return 'N/A';}
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getImportStats = (data: ImportDataSummary | null) => {
    if (!data) {return { total: 0, inserted: 0, updated: 0, errors: 0, isComparison: false };}
    
    // Check if it's a comparison result (test import)
    if (isComparisonResult(data)) {
      const summary = data.summary || {};
      return {
        total: summary.totalInFile || 0,
        inserted: summary.totalOk || 0,
        updated: summary.totalWeHave || 0,
        errors: summary.totalNotInRnic || 0,
        isComparison: true,
      };
    }
    
    // Regular import result
    return {
      total: data.total || 0,
      inserted: Object.keys(data.inserted || {}).length,
      updated: Object.keys(data.updated || {}).length,
      errors: Object.keys(data.errors || {}).length,
      isComparison: false,
    };
  };

  // Find the most recent test import per company (imports are already sorted by date DESC)
  const latestTestImportByCompany = useMemo(() => {
    const map = new Map<number, Import>();
    for (const imp of imports) {
      if (imp.isTestImport && imp.company?.id && !map.has(imp.company.id)) {
        map.set(imp.company.id, imp);
      }
    }
    return map;
  }, [imports]);

  const handleImportClick = (importItem: Import) => {
    // Check if this is a test import that is not the most recent one for this company
    if (importItem.isTestImport && importItem.company?.id) {
      const latestForCompany = latestTestImportByCompany.get(importItem.company.id);
      if (latestForCompany && importItem.id !== latestForCompany.id) {
        setPendingNavigationId(importItem.id);
        setWarningDialogOpen(true);
        return;
      }
    }
    navigate(ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(importItem.id)));
  };

  const handleWarningConfirm = () => {
    if (pendingNavigationId !== null) {
      navigate(ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(pendingNavigationId)));
    }
    setWarningDialogOpen(false);
    setPendingNavigationId(null);
  };

  const handleWarningClose = () => {
    setWarningDialogOpen(false);
    setPendingNavigationId(null);
  };

  return (
    <div className="mx-auto">
      {/* Warning Dialog for non-latest test import */}
      <NotLatestTestImport
        open={warningDialogOpen}
        onConfirm={handleWarningConfirm}
        onClose={handleWarningClose}
        context="history"
      />

      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <ClockIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Imports effectués
        </h1>
        <p className="text-gray-600">
          Historique de tous les imports réalisés (Neoteem, ImmOpen, RNIC Excel).
        </p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filtres :</span>
          </div>

          <CompanySearch
            onCompanySelect={(company) => setFilterCompanyId(company?.id ?? null)}
            placeholder="Rechercher par nom ou SIREN..."
            className="w-64"
            showClearButton
            resetKey={companySearchResetKey}
          />

          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-rgpd-blue focus:ring-rgpd-blue"
          >
            <option value="">Tous les types</option>
            {IMPORT_SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filterTestImport}
            onChange={(e) => setFilterTestImport(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-rgpd-blue focus:ring-rgpd-blue"
          >
            <option value="">Tous les modes</option>
            <option value="false">Base réelle</option>
            <option value="true">Base tampon</option>
          </select>

          {(filterSource || filterTestImport || filterCompanyId !== null) && (
            <button
              onClick={() => {
                setFilterSource('');
                setFilterTestImport('');
                setFilterCompanyId(null);
                setCompanySearchResetKey((prev) => prev + 1);
              }}
              className="text-sm text-rgpd-blue-dark hover:underline"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
            <span className="ml-3 text-gray-600">Chargement des imports...</span>
          </div>
        ) : imports.length === 0 ? (
          <div className="py-12 text-center">
            <Badge mode="warning" text="Aucun import trouvé" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Entreprise
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Résumé
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {imports.map((importItem) => {
                  const stats = getImportStats(importItem.data);
                  return (
                    <tr
                      key={importItem.id}
                      className="cursor-pointer transition-colors hover:bg-gray-50"
                      onClick={() => handleImportClick(importItem)}
                    >
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                        {formatDate(importItem.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {importItem.company?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {importItem.company?.siren || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge
                          mode={getImportSourceBadgeMode(importItem.importSource)}
                          text={getImportSourceLabel(importItem.importSource)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {importItem.isTestImport ? (
                          <span className="inline-flex items-center gap-1 text-sm text-orange-600">
                            <XCircleIcon className="h-4 w-4" />
                            Base tampon
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600">
                            <CheckCircleIcon className="h-4 w-4" />
                            Base réelle
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                        {importItem.createdBy?.firstname} {importItem.createdBy?.lastname}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-600">
                            Total: <strong>{stats.total}</strong>
                          </span>
                          {stats.isComparison ? (
                            // Comparison result labels
                            <>
                              {stats.inserted > 0 && (
                                <span className="text-green-600" title="OK (RNIC)">
                                  <CheckCircleIcon className="mr-0.5 inline h-3 w-3" />{stats.inserted}
                                </span>
                              )}
                              {stats.updated > 0 && (
                                <span className="text-blue-600" title="Déjà en base">
                                  ={stats.updated}
                                </span>
                              )}
                              {stats.errors > 0 && (
                                <span className="text-red-600" title="Pas dans RNIC (selon immat)">
                                  !{stats.errors}
                                </span>
                              )}
                            </>
                          ) : (
                            // Regular import result labels
                            <>
                              {stats.inserted > 0 && (
                                <span className="text-green-600" title="Insertions">
                                  +{stats.inserted}
                                </span>
                              )}
                              {stats.updated > 0 && (
                                <span className="text-blue-600" title="Modifications">
                                  ~{stats.updated}
                                </span>
                              )}
                              {stats.errors > 0 && (
                                <span className="text-red-600" title="Erreurs">
                                  !{stats.errors}
                                </span>
                              )}
                            </>
                          )}
                          <EyeIcon className="ml-2 h-4 w-4 text-rgpd-blue-dark" title="Voir les détails" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportHistory;
